import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

const PAYMENT_METHODS = ['Venmo', 'Zelle', 'Cash', 'Check', 'Other']
const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export default function RenterPortal({ role, staffId }) {
  const [rentals, setRentals] = useState([])
  const [staffList, setStaffList] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [paymentHistory, setPaymentHistory] = useState([])
  const [lateFees, setLateFees] = useState([])
  const [showMarkPaid, setShowMarkPaid] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [showAddRenter, setShowAddRenter] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState('Venmo')
  const [paymentNotes, setPaymentNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [currentMarkPaid, setCurrentMarkPaid] = useState(null)

  // Add renter form
  const [addStaffId, setAddStaffId] = useState('')
  const [addWeeklyRate, setAddWeeklyRate] = useState('')
  const [addDueDay, setAddDueDay] = useState(1)
  const [addLeaseStart, setAddLeaseStart] = useState('')
  const [addLeaseEnd, setAddLeaseEnd] = useState('')
  const [addNotes, setAddNotes] = useState('')

  // Edit form
  const [editWeeklyRate, setEditWeeklyRate] = useState('')
  const [editDueDay, setEditDueDay] = useState(1)
  const [editLeaseStart, setEditLeaseStart] = useState('')
  const [editLeaseEnd, setEditLeaseEnd] = useState('')
  const [editNotes, setEditNotes] = useState('')

  useEffect(() => {
    fetchRentals()
    fetchStaff()
  }, [])

  async function fetchRentals() {
    setLoading(true)
    if (role === 'owner' || role === 'admin') {
      const { data } = await supabase
        .from('rentals')
        .select(`*, staff (id, full_name, email)`)
        .eq('active', true)
        .order('created_at')
      if (data) setRentals(data)
    } else {
      const { data } = await supabase
        .from('rentals')
        .select(`*, staff (id, full_name, email)`)
        .eq('staff_id', staffId)
        .eq('active', true)
        .single()
      if (data) setRentals([data])
    }
    setLoading(false)
  }

  async function fetchStaff() {
    const { data } = await supabase
      .from('staff')
      .select('id, full_name, email')
      .eq('active', true)
      .order('full_name')
    if (data) setStaffList(data)
  }

  async function selectRental(rental) {
    setSelected(rental)
    const [paymentsRes, feesRes] = await Promise.all([
      supabase.from('rent_payments').select('*').eq('rental_id', rental.id).order('created_at', { ascending: false }),
      supabase.from('late_fees').select('*').eq('rental_id', rental.id).order('created_at', { ascending: false }),
    ])
    if (paymentsRes.data) setPaymentHistory(paymentsRes.data)
    if (feesRes.data) setLateFees(feesRes.data)
  }

  function openMarkPaid(rental) {
    setCurrentMarkPaid(rental)
    setPaymentMethod('Venmo')
    setPaymentNotes('')
    setShowMarkPaid(true)
  }

  async function markAsPaid() {
    if (!currentMarkPaid) return
    setSaving(true)
    await supabase.from('rent_payments').insert({
      rental_id: currentMarkPaid.id,
      amount: parseFloat(currentMarkPaid.weekly_rate || currentMarkPaid.monthly_rate || 0),
      payment_method: paymentMethod,
      notes: paymentNotes,
      paid_date: new Date().toISOString().split('T')[0],
    })
    setSaving(false)
    setShowMarkPaid(false)
    setCurrentMarkPaid(null)
    fetchRentals()
    if (selected?.id === currentMarkPaid.id) selectRental(selected)
    alert('Payment recorded!')
  }

  async function addRenter() {
    if (!addStaffId || !addWeeklyRate) {
      alert('Please select a team member and enter a weekly rate.')
      return
    }
    setSaving(true)
    const { error } = await supabase.from('rentals').insert({
      staff_id: addStaffId,
      weekly_rate: parseFloat(addWeeklyRate),
      monthly_rate: parseFloat(addWeeklyRate),
      due_day_of_week: addDueDay,
      lease_start_date: addLeaseStart || null,
      lease_end_date: addLeaseEnd || null,
      notes: addNotes || null,
      active: true,
      payment_frequency: 'weekly',
    })
    setSaving(false)
    if (!error) {
      setShowAddRenter(false)
      setAddStaffId('')
      setAddWeeklyRate('')
      setAddDueDay(1)
      setAddLeaseStart('')
      setAddLeaseEnd('')
      setAddNotes('')
      fetchRentals()
      alert('Renter added successfully!')
    } else {
      alert('Error adding renter: ' + error.message)
    }
  }

  function openEdit(rental) {
    setEditWeeklyRate((rental.weekly_rate || rental.monthly_rate || '').toString())
    setEditDueDay(rental.due_day_of_week ?? 1)
    setEditLeaseStart(rental.lease_start_date || '')
    setEditLeaseEnd(rental.lease_end_date || '')
    setEditNotes(rental.notes || '')
    setShowEdit(true)
  }

  async function saveEdit() {
    if (!selected) return
    setSaving(true)
    await supabase.from('rentals').update({
      weekly_rate: parseFloat(editWeeklyRate),
      due_day_of_week: editDueDay,
      lease_start_date: editLeaseStart || null,
      lease_end_date: editLeaseEnd || null,
      notes: editNotes || null,
    }).eq('id', selected.id)
    setSaving(false)
    setShowEdit(false)
    fetchRentals()
  }

  async function waiveFee(feeId) {
    if (window.confirm('Waive this late fee?')) {
      await supabase.from('late_fees').update({ waived: true }).eq('id', feeId)
      if (selected) selectRental(selected)
    }
  }

  function getRate(rental) {
    return rental?.weekly_rate || rental?.monthly_rate || 0
  }

  function isPaymentDueToday(rental) {
    return new Date().getDay() === (rental.due_day_of_week ?? 1)
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Renter's Portal</h1>
        {(role === 'owner' || role === 'admin') && (
          <button style={styles.addBtn} onClick={() => setShowAddRenter(true)}>
            + Add Renter
          </button>
        )}
      </div>

      <div style={styles.layout}>
        {/* Rentals list */}
        <div style={styles.listPanel}>
          {loading ? <div style={styles.empty}>Loading...</div> : rentals.length === 0 ? (
            <div style={styles.empty}>No active renters yet</div>
          ) : rentals.map(rental => (
            <div
              key={rental.id}
              style={{ ...styles.renterCard, ...(selected?.id === rental.id ? styles.renterCardActive : {}) }}>
              <div style={styles.renterCardTop} onClick={() => selectRental(rental)}>
                <div style={styles.renterAvatar}>
                  {rental.staff?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div style={styles.renterInfo}>
                  <div style={styles.renterName}>{rental.staff?.full_name}</div>
                  <div style={styles.renterDueDay}>Due every {DAYS_OF_WEEK[rental.due_day_of_week ?? 1]}</div>
                  {isPaymentDueToday(rental) && (
                    <div style={styles.dueTodayBadge}>⚠️ Due Today</div>
                  )}
                </div>
                <div style={styles.renterRate}>
                  <div style={styles.renterRateAmount}>${getRate(rental)}</div>
                  <div style={styles.renterRateSub}>/week</div>
                </div>
              </div>
              {(role === 'owner' || role === 'admin') && (
                <div style={styles.renterActions}>
                  <button style={styles.markPaidBtn} onClick={() => openMarkPaid(rental)}>
                    ✓ Mark as Paid
                  </button>
                  <button style={styles.editBtn} onClick={() => { selectRental(rental); openEdit(rental) }}>
                    Edit
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Detail panel */}
        {selected ? (
          <div style={styles.detailPanel}>
            <div style={styles.detailHeader}>
              <h2 style={styles.detailName}>{selected.staff?.full_name}</h2>
              <div style={styles.detailRate}>${getRate(selected)}/week</div>
            </div>

            <div style={styles.summaryGrid}>
              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Due Every</div>
                <div style={styles.summaryValue}>{DAYS_OF_WEEK[selected.due_day_of_week ?? 1]}</div>
              </div>
              {selected.lease_start_date && (
                <div style={styles.summaryItem}>
                  <div style={styles.summaryLabel}>Lease Start</div>
                  <div style={styles.summaryValue}>{selected.lease_start_date}</div>
                </div>
              )}
              {selected.lease_end_date && (
                <div style={styles.summaryItem}>
                  <div style={styles.summaryLabel}>Lease End</div>
                  <div style={styles.summaryValue}>{selected.lease_end_date}</div>
                </div>
              )}
              {selected.notes && (
                <div style={styles.summaryItem}>
                  <div style={styles.summaryLabel}>Notes</div>
                  <div style={styles.summaryValue}>{selected.notes}</div>
                </div>
              )}
            </div>

            {lateFees.length > 0 && (
              <div style={styles.section}>
                <div style={styles.sectionTitle}>LATE FEES</div>
                {lateFees.map(fee => (
                  <div key={fee.id} style={styles.feeRow}>
                    <div>
                      <div style={styles.feeAmount}>
                        ${fee.amount} {fee.waived && <span style={{ color: '#555', fontWeight: '400' }}>(waived)</span>}
                      </div>
                      <div style={styles.feeDate}>{new Date(fee.created_at).toLocaleDateString()}</div>
                    </div>
                    {!fee.waived && (role === 'owner' || role === 'admin') && (
                      <button style={styles.waiveBtn} onClick={() => waiveFee(fee.id)}>Waive</button>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div style={styles.section}>
              <div style={styles.sectionTitle}>PAYMENT HISTORY</div>
              {paymentHistory.length === 0 ? (
                <div style={styles.empty}>No payments recorded yet</div>
              ) : paymentHistory.map(p => (
                <div key={p.id} style={styles.paymentRow}>
                  <div>
                    <div style={styles.paymentAmount}>${p.amount}</div>
                    <div style={styles.paymentMethod}>{p.payment_method}</div>
                    {p.notes && <div style={styles.paymentNotes}>{p.notes}</div>}
                  </div>
                  <div style={styles.paymentDate}>
                    {p.paid_date || new Date(p.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>

            {(role === 'owner' || role === 'admin') && (
              <button style={styles.markPaidLargeBtn} onClick={() => openMarkPaid(selected)}>
                ✓ Mark as Paid
              </button>
            )}
          </div>
        ) : (
          <div style={styles.detailPanel}>
            <div style={styles.empty}>Select a renter to view details</div>
          </div>
        )}
      </div>

      {/* Add Renter Modal */}
      {showAddRenter && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Add Renter</h2>
              <button style={styles.closeBtn} onClick={() => setShowAddRenter(false)}>✕</button>
            </div>
            <p style={styles.modalSub}>Create a rental agreement for a team member.</p>

            <label style={styles.label}>Team Member *</label>
            <select
              style={styles.select}
              value={addStaffId}
              onChange={e => setAddStaffId(e.target.value)}>
              <option value="">Select team member...</option>
              {staffList.map(s => (
                <option key={s.id} value={s.id}>{s.full_name} — {s.email}</option>
              ))}
            </select>

            <label style={styles.label}>Weekly Rate ($) *</label>
            <input
              style={styles.input}
              value={addWeeklyRate}
              onChange={e => setAddWeeklyRate(e.target.value)}
              type="number"
              placeholder="e.g. 150"
            />

            <label style={styles.label}>Due Day of Week *</label>
            <div style={styles.pillRow}>
              {DAYS_OF_WEEK.map((day, i) => (
                <button
                  key={day}
                  style={{ ...styles.pill, ...(addDueDay === i ? styles.pillActive : {}) }}
                  onClick={() => setAddDueDay(i)}>
                  {day.slice(0, 3)}
                </button>
              ))}
            </div>

            <label style={styles.label}>Lease Start Date</label>
            <input
              style={styles.input}
              value={addLeaseStart}
              onChange={e => setAddLeaseStart(e.target.value)}
              placeholder="YYYY-MM-DD"
            />

            <label style={styles.label}>Lease End Date</label>
            <input
              style={styles.input}
              value={addLeaseEnd}
              onChange={e => setAddLeaseEnd(e.target.value)}
              placeholder="YYYY-MM-DD"
            />

            <label style={styles.label}>Notes</label>
            <textarea
              style={styles.textarea}
              value={addNotes}
              onChange={e => setAddNotes(e.target.value)}
              rows={3}
              placeholder="Any notes about this rental agreement..."
            />

            <button style={styles.saveBtn} onClick={addRenter} disabled={saving}>
              {saving ? 'Adding...' : 'ADD RENTER'}
            </button>
          </div>
        </div>
      )}

      {/* Mark as Paid Modal */}
      {showMarkPaid && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Mark Rent as Paid</h2>
              <button style={styles.closeBtn} onClick={() => setShowMarkPaid(false)}>✕</button>
            </div>
            {currentMarkPaid && (
              <p style={styles.modalSub}>
                {currentMarkPaid.staff?.full_name} — ${getRate(currentMarkPaid)}/week
              </p>
            )}

            <label style={styles.label}>Payment Method</label>
            <div style={styles.pillRow}>
              {PAYMENT_METHODS.map(m => (
                <button
                  key={m}
                  style={{ ...styles.pill, ...(paymentMethod === m ? styles.pillActive : {}) }}
                  onClick={() => setPaymentMethod(m)}>
                  {m}
                </button>
              ))}
            </div>

            <label style={styles.label}>Notes (optional)</label>
            <input
              style={styles.input}
              value={paymentNotes}
              onChange={e => setPaymentNotes(e.target.value)}
              placeholder="e.g. Paid via Venmo @username"
            />

            <button style={styles.saveBtn} onClick={markAsPaid} disabled={saving}>
              {saving ? 'Saving...' : 'CONFIRM PAYMENT'}
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEdit && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Edit Renter</h2>
              <button style={styles.closeBtn} onClick={() => setShowEdit(false)}>✕</button>
            </div>

            <label style={styles.label}>Weekly Rate ($)</label>
            <input
              style={styles.input}
              value={editWeeklyRate}
              onChange={e => setEditWeeklyRate(e.target.value)}
              type="number"
            />

            <label style={styles.label}>Due Day of Week</label>
            <div style={styles.pillRow}>
              {DAYS_OF_WEEK.map((day, i) => (
                <button
                  key={day}
                  style={{ ...styles.pill, ...(editDueDay === i ? styles.pillActive : {}) }}
                  onClick={() => setEditDueDay(i)}>
                  {day.slice(0, 3)}
                </button>
              ))}
            </div>

            <label style={styles.label}>Lease Start</label>
            <input
              style={styles.input}
              value={editLeaseStart}
              onChange={e => setEditLeaseStart(e.target.value)}
              placeholder="YYYY-MM-DD"
            />

            <label style={styles.label}>Lease End</label>
            <input
              style={styles.input}
              value={editLeaseEnd}
              onChange={e => setEditLeaseEnd(e.target.value)}
              placeholder="YYYY-MM-DD"
            />

            <label style={styles.label}>Notes</label>
            <textarea
              style={styles.textarea}
              value={editNotes}
              onChange={e => setEditNotes(e.target.value)}
              rows={3}
            />

            <button style={styles.saveBtn} onClick={saveEdit} disabled={saving}>
              {saving ? 'Saving...' : 'SAVE CHANGES'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const styles = {
  container: { padding: '32px', maxWidth: '1200px' },
  header: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: '24px',
  },
  title: {
    fontSize: '28px', fontWeight: '600', color: '#D4AF77',
    letterSpacing: '2px', textTransform: 'uppercase',
    fontFamily: 'Cormorant Garamond, serif',
  },
  addBtn: {
    padding: '10px 20px', backgroundColor: '#D4AF77', color: '#000',
    border: 'none', borderRadius: '2px', fontSize: '11px', fontWeight: '700',
    letterSpacing: '1.5px', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif',
  },
  layout: { display: 'grid', gridTemplateColumns: '340px 1fr', gap: '24px', alignItems: 'start' },
  listPanel: { display: 'flex', flexDirection: 'column', gap: '8px' },
  renterCard: {
    backgroundColor: '#111', border: '1px solid #1a1a1a',
    borderRadius: '2px', overflow: 'hidden',
  },
  renterCardActive: { borderColor: '#D4AF77' },
  renterCardTop: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '16px', cursor: 'pointer',
  },
  renterAvatar: {
    width: '40px', height: '40px', borderRadius: '50%',
    backgroundColor: '#D4AF77', display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: '13px', fontWeight: '700',
    color: '#000', flexShrink: 0,
  },
  renterInfo: { flex: 1 },
  renterName: { fontSize: '14px', fontWeight: '600', color: '#F5F0E8', marginBottom: '2px' },
  renterDueDay: { fontSize: '11px', color: '#555' },
  dueTodayBadge: {
    fontSize: '10px', color: '#ef4444', marginTop: '4px',
    backgroundColor: 'rgba(239,68,68,0.1)', padding: '2px 8px',
    borderRadius: '2px', display: 'inline-block',
  },
  renterRate: { textAlign: 'right' },
  renterRateAmount: { fontSize: '18px', fontWeight: '600', color: '#D4AF77' },
  renterRateSub: { fontSize: '10px', color: '#555' },
  renterActions: {
    display: 'flex', gap: '8px', padding: '10px 16px',
    borderTop: '1px solid #1a1a1a',
  },
  markPaidBtn: {
    flex: 2, padding: '8px', background: 'rgba(74,117,74,0.15)',
    border: '1px solid rgba(74,117,74,0.3)', color: '#4A754A',
    fontSize: '11px', fontWeight: '600', letterSpacing: '1px',
    cursor: 'pointer', borderRadius: '2px', fontFamily: 'Montserrat, sans-serif',
  },
  editBtn: {
    flex: 1, padding: '8px', background: '#1a1a1a', border: 'none',
    color: '#888', fontSize: '11px', fontWeight: '600', letterSpacing: '1px',
    cursor: 'pointer', borderRadius: '2px', fontFamily: 'Montserrat, sans-serif',
  },
  detailPanel: {
    backgroundColor: '#111', border: '1px solid #1a1a1a',
    borderRadius: '2px', padding: '24px', minHeight: '400px',
  },
  detailHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #1a1a1a',
  },
  detailName: { fontSize: '20px', fontWeight: '600', color: '#F5F0E8' },
  detailRate: {
    fontSize: '24px', fontWeight: '300', color: '#D4AF77',
    fontFamily: 'Cormorant Garamond, serif',
  },
  summaryGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '12px', marginBottom: '24px',
  },
  summaryItem: {
    backgroundColor: '#0a0a0a', border: '1px solid #1a1a1a',
    borderRadius: '2px', padding: '12px',
  },
  summaryLabel: {
    fontSize: '10px', color: '#555', letterSpacing: '1px',
    textTransform: 'uppercase', marginBottom: '4px',
  },
  summaryValue: { fontSize: '14px', fontWeight: '500', color: '#F5F0E8' },
  section: { marginBottom: '24px' },
  sectionTitle: {
    fontSize: '10px', fontWeight: '600', color: '#555',
    letterSpacing: '2px', marginBottom: '12px',
  },
  feeRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 0', borderBottom: '1px solid #1a1a1a',
  },
  feeAmount: { fontSize: '14px', fontWeight: '600', color: '#ef4444' },
  feeDate: { fontSize: '11px', color: '#444', marginTop: '2px' },
  waiveBtn: {
    padding: '6px 12px', background: 'none', border: '1px solid #333',
    color: '#555', fontSize: '11px', cursor: 'pointer', borderRadius: '2px',
    fontFamily: 'Montserrat, sans-serif',
  },
  paymentRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 0', borderBottom: '1px solid #1a1a1a',
  },
  paymentAmount: { fontSize: '15px', fontWeight: '600', color: '#4A754A' },
  paymentMethod: { fontSize: '11px', color: '#555', marginTop: '2px' },
  paymentNotes: { fontSize: '11px', color: '#444', marginTop: '2px' },
  paymentDate: { fontSize: '12px', color: '#555' },
  markPaidLargeBtn: {
    width: '100%', marginTop: '20px', padding: '14px',
    background: 'rgba(74,117,74,0.15)', border: '1px solid rgba(74,117,74,0.3)',
    color: '#4A754A', fontSize: '11px', fontWeight: '700', letterSpacing: '2px',
    cursor: 'pointer', borderRadius: '2px', fontFamily: 'Montserrat, sans-serif',
  },
  overlay: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    backgroundColor: '#111', border: '1px solid #222', borderRadius: '2px',
    padding: '32px', width: '100%', maxWidth: '500px',
    maxHeight: '90vh', overflowY: 'auto',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: '8px',
  },
  modalTitle: { fontSize: '18px', fontWeight: '600', color: '#F5F0E8' },
  modalSub: { fontSize: '13px', color: '#555', marginBottom: '16px' },
  closeBtn: {
    background: 'none', border: 'none', color: '#555',
    fontSize: '18px', cursor: 'pointer', padding: '4px',
  },
  label: {
    display: 'block', fontSize: '10px', fontWeight: '600', color: '#555',
    letterSpacing: '2px', textTransform: 'uppercase',
    marginBottom: '6px', marginTop: '16px', fontFamily: 'Montserrat, sans-serif',
  },
  select: {
    width: '100%', padding: '12px', backgroundColor: '#0a0a0a',
    border: '1px solid #222', borderRadius: '2px', color: '#F5F0E8',
    fontSize: '14px', fontFamily: 'Montserrat, sans-serif',
    outline: 'none', boxSizing: 'border-box',
  },
  input: {
    width: '100%', padding: '12px', backgroundColor: '#0a0a0a',
    border: '1px solid #222', borderRadius: '2px', color: '#F5F0E8',
    fontSize: '14px', fontFamily: 'Montserrat, sans-serif',
    outline: 'none', boxSizing: 'border-box',
  },
  textarea: {
    width: '100%', padding: '12px', backgroundColor: '#0a0a0a',
    border: '1px solid #222', borderRadius: '2px', color: '#F5F0E8',
    fontSize: '14px', fontFamily: 'Montserrat, sans-serif',
    outline: 'none', resize: 'vertical', boxSizing: 'border-box',
  },
  pillRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  pill: {
    padding: '8px 14px', background: 'none', border: '1px solid #222',
    color: '#555', fontSize: '11px', fontWeight: '600', letterSpacing: '1px',
    cursor: 'pointer', borderRadius: '2px', fontFamily: 'Montserrat, sans-serif',
  },
  pillActive: { backgroundColor: '#D4AF77', borderColor: '#D4AF77', color: '#000' },
  saveBtn: {
    marginTop: '24px', width: '100%', padding: '14px', backgroundColor: '#D4AF77',
    color: '#000', border: 'none', borderRadius: '2px', fontSize: '11px',
    fontWeight: '700', letterSpacing: '2px', cursor: 'pointer',
    fontFamily: 'Montserrat, sans-serif',
  },
  empty: { color: '#444', fontSize: '14px', padding: '24px', textAlign: 'center' },
}